import { useTranslation } from "react-i18next";

import { Invitation } from "simplydo/interfaces";
import { useMe } from "simplydo/react/hooks";
import { InvitationViewModel } from "simplydo/react/hooks/invitations/types";
import { Button } from "simplydo/react/ui/button";
import { CardContent } from "simplydo/react/ui/card";
import { H2, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";

import InviteLink from "../inviteLink";
import InviteeChip from "./InviteeChip";

type InvitationCardLayoutProps = {
  invitation: Invitation;
  vm: InvitationViewModel;
  cardView?: boolean;
};

const InvitationCardLayout = ({
  invitation,
  vm,
  cardView = true,
}: InvitationCardLayoutProps) => {
  const me = useMe();
  const { t } = useTranslation();
  const { createdByCurrentUser } = invitation;
  const { text } = vm;

  const dismissHandler = createdByCurrentUser
    ? vm.removeInvite
    : vm.rejectInvite;

  const showAccept =
    vm.showAccept ?? (!!me && !!invitation.canAccept && !invitation.acceptedAt);
  const showDismiss =
    vm.showDismiss ??
    (!!me && !!invitation.canRemove && !invitation.acceptedAt);

  const titleHeader = vm.link ? (
    <InviteLink to={vm.link}>
      <H4>{text.title}</H4>
    </InviteLink>
  ) : (
    <H4>{text.title}</H4>
  );

  return (
    <div
      style={
        cardView
          ? {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }
          : {}
      }
    >
      <div>
        {cardView ? (
          titleHeader
        ) : (
          <H2>
            <Icon icon={vm.icon} />
            {text.fullTitle}
          </H2>
        )}
        <Subheader>
          <InviteeChip
            invitation={invitation}
            youInvited={text.youInvited}
            hasInvitedYou={text.hasInvitedYou}
          />
        </Subheader>
      </div>
      <CardContent>
        <div
          style={
            cardView
              ? {}
              : { marginTop: 20, display: "flex", justifyContent: "flex-end" }
          }
        >
          {invitation.acceptedAt && vm.link && text.goTo ? (
            <InviteLink to={vm.link}>
              <Button variant="outline" color="green">
                {text.goTo}
              </Button>
            </InviteLink>
          ) : null}
          {!invitation.acceptedAt && (
            <>
              {vm.isInvalid ? (
                <Message color="red" style={{ width: "100%" }}>
                  {text.invalid}
                </Message>
              ) : null}
              {showDismiss && dismissHandler ? (
                <Button
                  variant="outline"
                  color={createdByCurrentUser ? undefined : "red"}
                  onClick={dismissHandler}
                >
                  {text.dismiss}
                </Button>
              ) : null}
              {showAccept && vm.acceptInvite ? (
                <Button
                  variant="outline"
                  color="green"
                  onClick={vm.acceptInvite}
                >
                  {t("invitations.accept")}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </CardContent>
    </div>
  );
};

export default InvitationCardLayout;
